"use client";

import Image from "next/image";
import { Icon } from "@iconify/react";
import ProductHighlights from "./ProductHighlights";
import type { UnifiedProduct } from "@/lib/product-data/product.types";
import Link from "next/link";
import { motion } from "motion/react";
import ImageSlider from "../Shared/ImageSlider";
import { useState } from "react";

export default function ProductDetailHero({
  product,
}: {
  product: UnifiedProduct;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <section className="relative pt-20 pb-20 px-6 bg-hero dark:bg-midnight overflow-hidden">
      <div className="max-w-[83.75rem] h-full mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 relative z-10">
        {/* Left: Sticky Image Slider */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white sticky top-0 h-fit dark:bg-white/5 rounded-[3rem] border border-border dark:border-white/10 p-8 overflow-hidden shadow-sm"
        >
          <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 to-transparent rounded-[3rem]"></div>{" "}
          <ImageSlider images={product.images} altPrefix={product.name} />
        </motion.div>

        {/* Right: Info */} 
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
        >
          <div className="inline-block mb-4 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold tracking-widest uppercase">
            {product.subtitle ?? product.tagline ?? "Product"}
          </div>

          <h1 className="text-40 md:text-6xl font-bold mb-4 leading-tight">
            {product.name}
          </h1>

          {product.tagline && (
            <p className="text-xl text-primary font-medium mb-6 border-l-4 border-primary pl-4">
              {product.tagline}
            </p>
          )}

          {/* Collapsible Description */}
          <div className="mb-8">
            <p
              className={`text-lg text-muted dark:text-white/70 leading-relaxed whitespace-pre-wrap ${
                !isExpanded ? "line-clamp-4" : ""
              }`}
            >
              {product.description}
            </p>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-primary font-bold text-sm mt-2 flex items-center gap-1 hover:underline"
            >
              {isExpanded ? "Read Less" : "Read More"}
              <Icon icon={isExpanded ? "ion:chevron-up" : "ion:chevron-down"} />
            </button>
          </div>

          <ProductHighlights highlights={product.highlights} />

          <div className="flex gap-4 mt-8">
            <Link
              href="/contact"
              className="bg-primary text-white px-8 py-4 rounded-xl font-bold hover:bg-midnight transition-all shadow-lg flex items-center gap-2"
            >
              <Icon icon="ion:mail" width="20" /> Request Quote
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
